/**
 * Reglas del alta self-service de una lista CAD.
 * Contrato: `argenlotapi/docs/frontend/FRONTEND_CAD_UPLOAD.md`.
 * Copy de producto: el usuario ve **lista** y **lote**, nunca showroom/parcela.
 */

import { countCadSellableLots } from './cadMasterplan';

/** Tope del MVP (el GET lo repite en `requirements.max_bytes`). */
export const CAD_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
export const CAD_UPLOAD_ACCEPT = '.dxf';
export const CAD_UPLOAD_NAME_MAX = 200;

/** Content-Type por defecto del PUT; el real sale de `/getcaduploadurl`. */
export const CAD_UPLOAD_DEFAULT_CONTENT_TYPE = 'application/octet-stream';

/** Requisitos que se muestran **antes** del drop. */
export const CAD_UPLOAD_REQUIREMENTS = [
  'Un archivo .dxf por vez (no DWG ni planillas).',
  'Los lotes tienen que ser polilíneas cerradas.',
  'En metros, a escala real (1:1), para calcular la superficie de cada lote.',
  'Superficie de cada lote entre 200 y 5.000 m².',
  'Hasta 15 MB.',
];

/** Mensajes del overlay mientras corre el alta. */
export const CAD_UPLOAD_STAGE_PREPARE = 'Preparando la carga…';
export const CAD_UPLOAD_STAGE_UPLOAD = 'Subiendo el plano…';
export const CAD_UPLOAD_STAGE_PARSE = 'Leyendo los lotes y calculando la superficie de cada uno…';

/**
 * Requisitos a mostrar: los del API si vinieron, con los del MVP de respaldo.
 *
 * @param {object|null} requirements  `data.requirements` de `/getcaduploadurl`
 * @returns {string[]}
 */
export function cadUploadRequirementLines(requirements) {
  const notes = requirements?.notes;
  if (Array.isArray(notes)) {
    const clean = notes
      .map((note) => (typeof note === 'string' ? note.trim() : ''))
      .filter(Boolean);
    if (clean.length > 0) return clean;
  }
  return CAD_UPLOAD_REQUIREMENTS;
}

/**
 * Bytes máximos aceptados: el tope del API si lo mandó, si no el del MVP.
 * @param {object|null} requirements
 * @returns {number}
 */
export function cadUploadMaxBytes(requirements) {
  const max = Number(requirements?.max_bytes);
  return Number.isFinite(max) && max > 0 ? max : CAD_UPLOAD_MAX_BYTES;
}

/**
 * @param {number|null|undefined} bytes
 * @returns {string} `12,4 MB`
 */
export function formatCadUploadSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toLocaleString('es-AR', { maximumFractionDigits: 1 })} MB`;
}

export function hasDxfExtension(filename) {
  return /\.dxf$/i.test(String(filename || '').trim());
}

/**
 * Validación de cliente antes de pedir la URL prefirmada. El parse del API
 * sigue siendo la validación real: acá solo se evitan viajes obvios.
 *
 * @param {File|null} file
 * @param {{ maxBytes?: number }} [options]
 * @returns {{ valid: boolean, message: string }}
 */
export function validateCadUploadFile(file, { maxBytes = CAD_UPLOAD_MAX_BYTES } = {}) {
  if (!file) {
    return { valid: false, message: 'Elegí un archivo .dxf para continuar.' };
  }
  if (/\.dwg$/i.test(file.name || '')) {
    return { valid: false, message: 'Solo se aceptan archivos .dxf (no DWG).' };
  }
  if (!hasDxfExtension(file.name)) {
    return { valid: false, message: 'El archivo tiene que ser .dxf.' };
  }
  if (Number(file.size) === 0) {
    return { valid: false, message: 'El archivo está vacío.' };
  }
  if (Number(file.size) > maxBytes) {
    return {
      valid: false,
      message: `El archivo pesa más de ${formatCadUploadSize(maxBytes)}.`,
    };
  }
  return { valid: true, message: '' };
}

/**
 * Nombre sugerido de la lista a partir del archivo: sin extensión, con
 * separadores en espacios y capitalizado. `ALMASIERRA.dxf` → `Almasierra`.
 *
 * @param {string} filename
 * @returns {string}
 */
export function suggestCadListName(filename) {
  const base = String(filename || '')
    .trim()
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!base) return '';
  const normalized = base === base.toUpperCase() ? base.toLowerCase() : base;
  return normalized
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ')
    .slice(0, CAD_UPLOAD_NAME_MAX);
}

/**
 * Preview del parse en el formato que ya consume el plano canvas
 * (`ShowroomPlanStage`): mismas claves que `GET /getcadproject`.
 *
 * @param {object|null} preview  `data` de `/parsecadproject`
 * @returns {object|null}
 */
export function cadPreviewToPlanProject(preview) {
  if (!preview) return null;
  return {
    slug: 'cad-upload-preview',
    name: preview.report?.filename || 'Plano nuevo',
    display_mode: preview.display_mode || 'canvas',
    srid: Number.isFinite(Number(preview.srid)) ? Number(preview.srid) : 0,
    geometry_source: preview.geometry_source || 'geom',
    bounds: preview.bounds || null,
    parcels: preview.parcels || { type: 'FeatureCollection', features: [] },
  };
}

/**
 * Cuántos lotes trae el preview: el informe manda, el GeoJSON es respaldo.
 * @param {object|null} preview
 * @returns {number}
 */
export function countCadPreviewParcels(preview) {
  const features = preview?.parcels?.features;
  if (Array.isArray(features) && features.length > 0) {
    return countCadSellableLots(features);
  }
  const accepted = Number(preview?.report?.accepted);
  if (Number.isFinite(accepted)) return accepted;
  return 0;
}

/**
 * Líneas del informe para la UI de revisión. Sin jerga: “lotes”, no parcelas.
 *
 * @param {object|null} preview
 * @returns {{ label: string, value: string }[]}
 */
export function cadPreviewSummaryRows(preview) {
  const report = preview?.report || {};
  const rows = [];
  const accepted = Number(report.accepted);
  if (Number.isFinite(accepted)) {
    rows.push({ label: 'Lotes detectados', value: String(accepted) });
  }
  const median = Number(report.area_m2?.median);
  if (Number.isFinite(median)) {
    rows.push({
      label: 'Superficie promedio',
      value: `${median.toLocaleString('es-AR', { maximumFractionDigits: 0 })} m²`,
    });
  }
  return rows;
}

/**
 * Avisos a pintar: los del informe más el de `looks_like: 'revisar'`, que
 * no bloquea (el usuario mira el plano y decide).
 *
 * @param {object|null} preview
 * @returns {string[]}
 */
export function cadPreviewWarnings(preview) {
  const report = preview?.report || {};
  const warnings = Array.isArray(report.warnings)
    ? report.warnings.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean)
    : [];
  if (report.looks_like === 'revisar') {
    warnings.unshift('Revisá el plano: puede que no sea un loteo. Si no se ven los lotes, cancelá y subí otro archivo.');
  }
  return warnings;
}

/**
 * Otras capas que el informe ofrece para reintentar el parse sin volver a
 * subir el archivo.
 *
 * @param {object|null} preview
 * @returns {string[]}
 */
export function cadPreviewLayerCandidates(preview) {
  const candidates = preview?.report?.layer_candidates;
  if (!Array.isArray(candidates)) return [];
  const current = preview?.report?.layer;
  return candidates
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      const name = item?.name ?? item?.layer;
      return typeof name === 'string' ? name.trim() : '';
    })
    .filter((name) => name && name !== current);
}
